//--------------------------------------------------------------------------------------------------
import { PureComponent, CSSProperties, ReactElement } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { isDefined } from '@Utils/Micro';
//--------------------------------------------------------------------------------------------------
/**
 * Timeline info item's footer.
 *
 * @export
 * @interface TimelineInfoItemFooter
 */
export interface TimelineInfoItemFooter {
    /**
     * Footer's content.
     *
     * @type {string}
     * @memberof TimelineInfoItemFooter
     */
    content: string;

    /**
     * Passed HTML.
     *
     * @type {string}
     * @memberof TimelineInfoItemFooter
     */
    markup?: ReactElement[];
}
//--------------------------------------------------------------------------------------------------
/**
 * Timeline info item's body.
 *
 * @export
 * @interface TimelineInfoItemBody
 */
export interface TimelineInfoItemBody {
    /**
     * Body's content.
     *
     * @type {string}
     * @memberof TimelineInfoItemBody
     */
    content: string;

    /**
     * Passed HTML.
     *
     * @type {ReactElement }
     * @memberof TimelineInfoItemHeader
     */
    markup?: ReactElement[];
}
//--------------------------------------------------------------------------------------------------
/**
 * Timeline info item's header.
 *
 * @export
 * @interface TimelineInfoItemHeader
 */
export interface TimelineInfoItemHeader {
    /**
     * Header's URL.
     *
     * @type {string}
     * @memberof TimelineInfoItemHeader
     */
    url: string;

    /**
     * Header's title.
     *
     * @type {string}
     * @memberof TimelineInfoItemHeader
     */
    title: string;

    /**
     * Header's content.
     *
     * @type {string}
     * @memberof TimelineInfoItemHeader
     */
    content: string;

    /**
     * Passed HTML.
     *
     * @type {ReactElement }
     * @memberof TimelineInfoItemHeader
     */
    markup?: ReactElement[];
}
//--------------------------------------------------------------------------------------------------
/**
 * Timeline's info item.
 *
 * @export
 * @interface TimelineInfoItem
 */
export interface TimelineInfoItem {
    /**
     * Any font-awesome icon as a marker for the timeline element.
     *
     * @type {string}
     * @memberof TimelineInfoItem
     */
    icon: string;

    /**
     * Color of the marker.
     *
     * @type {string}
     * @memberof TimelineInfoItem
     */
    iconTheme: string;

    /**
     * Date/Time for the element.
     *
     * @type {string}
     * @memberof TimelineInfoItem
     */
    time: string;

    /**
     * Header section of the item.
     *
     * @type {TimelineInfoItemHeader}
     * @memberof TimelineInfoItem
     */
    header: TimelineInfoItemHeader;

    /**
     * Body of the item.
     *
     * @type {TimelineInfoItemBody}
     * @memberof TimelineInfoItem
     */
    body?: TimelineInfoItemBody;

    /**
     * Footer section of the item.
     *
     * @type {TimelineInfoItemFooter}
     * @memberof TimelineInfoItem
     */
    footer?: TimelineInfoItemFooter;
}
//--------------------------------------------------------------------------------------------------
/**
 * Timeline's info object.
 *
 * @export
 * @interface TimelineInfoObject
 */
export interface TimelineInfoObject {
    /**
     * Beginning of one element in the timeline.
     *
     * @type {string}
     * @memberof TimelineInfoObject
     */
    startDate: string;

    /**
     * End time of the element in the timeline.
     *
     * @type {string}
     * @memberof TimelineInfoObject
     */
    endDate: string;

    /**
     * Array of objects containing data to be rendered for each individual element in the timeline.
     *
     * @type {TimelineInfoItem[]}
     * @memberof TimelineInfoObject
     */
    items: TimelineInfoItem[];
}
//--------------------------------------------------------------------------------------------------
/**
 * Timeline tile's component's properties.
 *
 * @export
 * @interface TimelineProps
 */
export interface TimelineProps {
    timelineInfo: TimelineInfoObject[];
}
//--------------------------------------------------------------------------------------------------
const timelineStyle_ul: CSSProperties = {
    position: "relative",
    margin: "0 0 24px 0",
    padding: 0,
    listStyle: "none"
};
const timelineStyle_ul_li: CSSProperties = {
    position: "relative",
    marginRight: 8,
    marginBottom: 8,
    display: "table",
    width: '100%'
};
const timelineStyle_ul_li_fa: CSSProperties = {
    width: 24,
    height: 24,
    fontSize: 12,
    lineHeight: "24px",
    position: "absolute",
    color: "white",
    background: "#d2d6de",
    borderRadius: "50%",
    textAlign: "center",
    left: 2,
    top: 0
};
const timelineStyle_ul_li_label: CSSProperties = {
    padding: 4,
    color: "#212121",
    display: "inline-block",
    backgroundColor: "#e3f2fd",
    borderRadius: 4,
    marginLeft: 36,
    width: 'calc(100% - 44px)'
};
//--------------------------------------------------------------------------------------------------

/**
 * Timeline tile component class.
 *
 * @export
 * @class Timeline
 * @extends {PureComponent<TimelineProps>}
 */
export class Timeline extends PureComponent<TimelineProps> {
    constructor(prop: TimelineProps) { super(prop); }
    //----------------------------------------------------------------------------------------------
    render() {
        var timelineInfo = [];
        _.map(this.props.timelineInfo, (timelineElement, iterator) => {
            if (timelineElement.endDate) {
                let time = timelineElement.endDate;
                try {
                    let t = moment.utc(timelineElement.endDate).local();
                    if (!t.isValid()) {
                        t = moment.utc(timelineElement.endDate, 'x').local();
                    }
                    time = t.toLocaleString();
                } catch (ex) { }
                timelineInfo.push(
                    <li key={"label1" + iterator} style={timelineStyle_ul_li}>
                        <span style={timelineStyle_ul_li_label}>
                            {time}
                        </span>
                    </li>
                );
            }

            _.map(timelineElement.items, (item, i) => {
                let time = item.time;
                try {
                    let t = moment.utc(item.time).local();
                    if (!t.isValid()) {
                        t = moment.utc(item.time, 'x').local();
                    }
                    time = t.toLocaleString();
                } catch (ex) { }
                timelineInfo.push(
                    <TimelineItem
                        key={"item" + iterator + '.' + i}
                        icon={item.icon}
                        iconTheme={item.iconTheme}
                        time={time}
                        header={item.header}
                        body={item.body}
                        footer={item.footer} >
                    </TimelineItem>
                )
            });

            if (timelineElement.startDate) {
                let time = timelineElement.startDate;
                try {
                    let t = moment.utc(timelineElement.startDate).local();
                    if (!t.isValid()) {
                        t = moment.utc(timelineElement.startDate, 'x').local();
                    }
                    time = t.toLocaleString();
                } catch (ex) { }
                timelineInfo.push(
                    <li key={"label2" + iterator} style={timelineStyle_ul_li}>
                        <span style={timelineStyle_ul_li_label}>
                            {time}
                        </span>
                    </li>
                );
            }
        });
        return <div style={{ width: "100%", float: "left", padding: 8 }}>
            <ul style={timelineStyle_ul}>
                <div style={{
                    content: '',
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: "#ddd",
                    left: 12,
                    margin: 0
                }}></div>
                {timelineInfo}
                <li style={timelineStyle_ul_li}>
                    <i className="fa fa-clock-o" style={timelineStyle_ul_li_fa}></i>
                </li>
            </ul>
        </div >
    }
}
//--------------------------------------------------------------------------------------------------
interface TimelineItemProps {
    icon: string;
    iconTheme: string;
    time: string;
    header: TimelineInfoItemHeader;
    body?: TimelineInfoItemBody;
    footer?: TimelineInfoItemFooter;
}
//--------------------------------------------------------------------------------------------------
class TimelineItem extends PureComponent<TimelineItemProps> {
    constructor(prop: TimelineItemProps) { super(prop); }
    //----------------------------------------------------------------------------------------------
    render() {
        return (
            <li style={{ ...timelineStyle_ul_li, width: "100%" }}>
                <i className={this.props.icon + " " + this.props.iconTheme}
                    style={{
                        ...timelineStyle_ul_li_fa,
                        marginTop: 6
                    }}></i>

                <div style={{
                    backgroundColor: '#fafafa',
                    marginTop: 0,
                    color: "#444",
                    marginLeft: 36,
                    marginRight: 8,
                    padding: 0,
                    position: "relative"
                }}>
                    <span style={{
                        color: "#999",
                        float: "right",
                        padding: 8,
                        fontSize: 12
                    }}>
                        <i className="fa fa-clock-o"
                            style={{
                                margin: '0 4px',
                                verticalAlign: 'middle'
                            }}></i>
                        {this.props.time}
                    </span>

                    <h3 style={{
                        margin: 0,
                        color: "#555",
                        padding: 8,
                        fontSize: 14
                    }}>
                        {
                            (isDefined(this.props.header.url)) ?
                                <a href={this.props.header.url} target="_blank">{this.props.header.title}</a>
                                :
                                this.props.header.title
                        }
                        {this.props.header.content}
                    </h3>

                    {
                        (isDefined(this.props.body) && (
                            isDefined(this.props.body.content) ||
                            isDefined(this.props.body.markup)
                        )) ?
                            <div style={{ padding: 8 }}>
                                {this.props.body.content}
                                {this.props.body.markup}
                            </div>
                            : null
                    }

                    {
                        (isDefined(this.props.footer) && (
                            isDefined(this.props.footer.content) ||
                            isDefined(this.props.footer.markup)
                        )) ?
                            <div style={{ padding: 8 }}>
                                {this.props.footer.content}
                                {this.props.footer.markup}
                            </div>
                            : null
                    }
                </div>
            </li >
        )
    }
}
//--------------------------------------------------------------------------------------------------
